import type { QueryVar } from '@openx/types/serverSideCriteria.ts';
import type { AllTableCriteria, SortCriteria } from '@openx/types/tableCriteria.ts';
import { parseMultiValueFilter } from '@openx/utils/lib/multiValueFilter.ts';
import { setNestedValue } from '@openx/utils/lib/setNestedValue.ts';

export type ByValueFilter = {
  field: string;
  values: (string | number | null | undefined)[];
}[];

export type ByPhraseFilter = {
  fields: string[];
  phrase: string;
};

export type FiltersT = {
  byPhrase?: ByPhraseFilter;
  phrase?: string;
  byValue?: ByValueFilter;
};

type ByPhraseQueryType =
  | {
      [column: string]: {
        _ilike: string;
      };
    }
  | {
      [column: string]: {
        name: {
          _ilike: string;
        };
      };
    };

type ByValueQueryType = Record<string, unknown>;

//from serverSideCriteriaProcessing.ts
const segmentPhraseFilters = ['account', 'provider'];
const audiencePhraseFilters = ['account'];
const dealListMultiValueFilters = new Set([
  // `account` is expanded at call sites (OA DealsList, segment adjustCriteriaFilters) so Data Partner names
  // with commas are not split twice (segmentPhraseFilters + deal CSV).
  'createdBy',
  'status',
  'deal_id',
  'exchange_deal_id',
  'package_uid',
  'pmp_deal_type',
]);

const numericSegmentFilters = new Set(['computed_price_cpm', 'computed_price_pom', 'reach']);
const numericAudienceFilters = new Set(['addressable_reach']);

const dateFilters = new Set(['created_date', 'updated_date', 'expiration_date', 'derived_expiry_date']);

const fallbackSort = [{ id: 'asc_nulls_first' }];

const convertSort = (sort: string) => {
  if (sort === 'asc') return 'asc_nulls_first';
  if (sort === 'desc') return 'desc_nulls_last';
  return sort;
};

export const sortStage = (sort: SortCriteria | undefined) => {
  if (sort?.column && sort.direction) {
    const queryDirection = convertSort(sort.direction);
    const sortCriteria = { [sort.column]: queryDirection };

    const valuePath = sort.valuePath?.[sort.column];
    if (valuePath?.length) {
      setNestedValue(sortCriteria, [sort.column, ...valuePath], queryDirection);
    }

    return sortCriteria;
  }
  return {};
};

export const filterSubstageValue = (filters?: FiltersT | null): { _and: ByValueQueryType[] } => {
  const byValue: { _and: ByValueQueryType[] } = { _and: [] };

  const getNormalizedValues = (field: string, values: (string | number | null | undefined)[]) => {
    if (!dealListMultiValueFilters.has(field)) {
      return values;
    }

    if (values.length !== 1 || typeof values[0] !== 'string') {
      return values;
    }

    return parseMultiValueFilter(values[0]);
  };

  if (filters?.byValue) {
    byValue._and = filters.byValue
      .filter(valFil => (valFil.values || []).length > 0)
      .filter(valFil => (valFil.field || '').length > 0)
      .map(({ field, values }) => {
        const normalizedValues = getNormalizedValues(field, values);

        if (segmentPhraseFilters.includes(field)) {
          return {
            [field]: {
              name: {
                _in: normalizedValues,
              },
            },
          };
        }

        if (field === 'regions') {
          return {
            regions: {
              region_code: {
                _in: values,
              },
            },
          };
        }

        if (field === 'countries') {
          return {
            countries: {
              country_code: {
                _in: values,
              },
            },
          };
        }

        if (dateFilters.has(field)) {
          return {
            [field]: {
              _gte: values[0],
              _lte: values[1],
            },
          };
        }

        if (field === 'direct_audience_provider' || field === 'sub_type') {
          return {
            [field]: {
              _in: values.map(val => (typeof val === 'string' ? val.toLowerCase() : 'openaudience')),
            },
          };
        }

        if (field === 'createdBy') {
          return {
            user_id: {
              _in: normalizedValues,
            },
          };
        }

        if (numericSegmentFilters.has(field) || numericAudienceFilters.has(field)) {
          const [min, max, operator] = values;

          if (operator === '__range') {
            return {
              [field]: {
                _gte: min,
                _lte: max,
              },
            };
          }

          if (operator === '__lte') {
            return {
              [field]: {
                _lte: max,
              },
            };
          }

          if (operator === '__gte') {
            return {
              [field]: {
                _gte: min,
              },
            };
          }

          if (typeof operator === 'string' && operator.length > 1) {
            return {
              [field]: {
                [operator.slice(1)]: max,
              },
            };
          }
        }

        return {
          [field]: {
            _in: normalizedValues,
          },
        };
      });
  }

  return byValue;
};

export const filterSubstagePhrase = (filters?: FiltersT | null): { _or: ByPhraseQueryType[] } => {
  const byPhrase: { _or: ByPhraseQueryType[] } = { _or: [] };

  if (filters?.byPhrase) {
    const { fields, phrase }: { fields: string[]; phrase?: string } = filters.byPhrase;
    const containsSegmentPhraseFilters = segmentPhraseFilters.every(filter => fields.includes(filter));
    const containsAudiencePhraseFilters = audiencePhraseFilters.every(filter => fields.includes(filter));

    if (phrase && (containsSegmentPhraseFilters || containsAudiencePhraseFilters)) {
      const trimmedPhrase = phrase.trim();

      byPhrase._or = fields.map(fieldKey => {
        if (segmentPhraseFilters.includes(fieldKey)) {
          return {
            [fieldKey]: {
              name: {
                _ilike: `%${trimmedPhrase}%`,
              },
            },
          };
        }

        return {
          [fieldKey]: {
            _ilike: `%${trimmedPhrase}%`,
          },
        };
      });

      return byPhrase;
    }

    if (phrase && fields) {
      byPhrase._or = fields.map(fieldKey => ({
        [fieldKey]: {
          _ilike: `%${phrase.trim()}%`,
        },
      }));
    }
  }

  return byPhrase;
};

//from serverSideCriteriaProcessing.ts
export const filterStage = (filters?: FiltersT | null): QueryVar['where'] | undefined => {
  const byPhrase = filterSubstagePhrase(filters);
  const byValue = filterSubstageValue(filters);
  const _and: Record<string, unknown>[] = [];
  if (byPhrase._or.length > 0) {
    _and.push(byPhrase);
  }
  if (byValue._and.length > 0) {
    _and.push(byValue);
  }

  return _and.length > 0 ? ({ _and } as QueryVar['where']) : undefined;
};

function serverSideCriteriaProcessingImpl<T extends QueryVar = QueryVar, F extends FiltersT = FiltersT>(
  criteria: AllTableCriteria<F>,
  initSort?: AllTableCriteria<F>['sort'][]
): T {
  const { filters, sort, pagination } = criteria;
  const queryVar: QueryVar = {
    limit: pagination?.pageSize ?? null,
    offset: 0,
    order_by: null,
    where: null,
  };
  const pageIndex = (pagination?.pageNumber ?? 1) - 1;

  queryVar.where = filterStage(filters);

  const defaultSort = initSort?.length && initSort.map(sortCriteria => sortStage(sortCriteria));
  const currentSort = sortStage(sort);

  queryVar.order_by = defaultSort ? [currentSort, ...defaultSort, ...fallbackSort] : [currentSort, ...fallbackSort];

  // paginationStage()
  if (pagination) {
    if (pageIndex * pagination.pageSize < pagination.totalCount) {
      queryVar.offset = pagination.pageSize * pageIndex;
    }
  }

  return queryVar as T;
}

/**
 * Mutable plain-object re-export for Cypress + Vite + Sinon compatibility.
 *
 * Native ESM Module Namespace Objects have non-configurable property descriptors,
 * so Sinon throws "ES Modules cannot be spied" when a test calls
 * `cy.spy(namespace, 'method')`. This plain object is configurable, and the
 * delegating wrapper below routes all real call sites through it, so a spy placed
 * on `_cjsExports.serverSideCriteriaProcessing` intercepts actual invocations.
 */
export const _cjsExports = {
  serverSideCriteriaProcessing: serverSideCriteriaProcessingImpl,
};

export function serverSideCriteriaProcessing<T extends QueryVar = QueryVar, F extends FiltersT = FiltersT>(
  criteria: AllTableCriteria<F>,
  initSort?: AllTableCriteria<F>['sort'][]
): T {
  return _cjsExports.serverSideCriteriaProcessing(criteria, initSort) as unknown as T;
}
